import { Component, ChangeDetectionStrategy, computed, effect, inject, signal, untracked } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';

import { GraphQLClient } from '../../graphql/client';
import { MutationError, resolveErrorMessage } from '@branduals/graphql-errors';
import { VALIDATE_ASSET_COMPLIANCE, ValidateAssetComplianceResponse } from '../../graphql/compliance-operations';
import { ComplianceCheckDialogService } from './compliance-check-dialog.service';
import { StatusBadgeComponent, StatusBadgeTone } from '../status-badge/status-badge.component';
import { AssetComplianceValidation, ComplianceScanStatus, COMPLIANCE_SKIP_REASONS } from '../../models/compliance.models';
import { DisplayDatePipe } from '../../pipes/display-date.pipe';

/** Status tone map — mirrors the wire enum exactly (COMPLIANT/VIOLATION/WARNING/SKIPPED/STALE). */
const STATUS_TONES: Record<ComplianceScanStatus, StatusBadgeTone> = {
    COMPLIANT: 'success',
    VIOLATION: 'danger',
    WARNING: 'warning',
    SKIPPED: 'neutral',
    STALE: 'neutral',
};

/**
 * Per-asset "Check compliance" results: runs the NON-PERSISTING `validateAssetCompliance`
 * query on open and lists one row per enabled rule (skips included — honest coverage). Advisory
 * only: nothing is written, so this never becomes "the latest check" anywhere. Globally mounted
 * (app.component.html); reachable from the file-preview overlay, hence the `.p-dialog-mask`
 * z-index lift in styles.scss (PLAYBOOK_FRONTEND §5).
 */
@Component({
    selector: 'app-compliance-check-dialog',
    standalone: true,
    imports: [DisplayDatePipe, DialogModule, TranslateModule, FontAwesomeModule, StatusBadgeComponent],
    templateUrl: './compliance-check-dialog.component.html',
    styleUrls: ['./compliance-check-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplianceCheckDialogComponent {
    protected readonly faSpinnerThird = faSpinnerThird;

    protected readonly dialog = inject(ComplianceCheckDialogService);
    private readonly graphql = inject(GraphQLClient);
    private readonly messageService = inject(MessageService);
    private readonly translate = inject(TranslateService);

    protected readonly loading = signal(false);
    protected readonly result = signal<AssetComplianceValidation | null>(null);

    protected readonly hasRules = computed(() => (this.result()?.rulesEvaluated ?? 0) > 0);

    constructor() {
        // Re-run the check on every open (fresh advisory read).
        effect(() => {
            const visible = this.dialog.visible();
            const assetId = this.dialog.assetId();
            if (visible && assetId) untracked(() => void this.check(assetId));
        });
    }

    protected statusTone(status: ComplianceScanStatus): StatusBadgeTone {
        return STATUS_TONES[status] ?? 'neutral';
    }

    /** Skip codes are machine values the client translates; a NEW server code reaching an old
     *  bundle must render the generic line, never a raw i18n key (forward-compat fallback). */
    protected skipReasonKey(reason: string): string {
        return (COMPLIANCE_SKIP_REASONS as readonly string[]).includes(reason)
            ? `compliance.check.skipReason.${reason}`
            : 'compliance.check.skipReason.GENERIC';
    }

    protected onVisibleChange(visible: boolean): void {
        if (!visible) this.dialog.close();
    }

    private async check(assetId: string): Promise<void> {
        this.loading.set(true);
        this.result.set(null);
        try {
            const r = await this.graphql.queryAsync<ValidateAssetComplianceResponse>(
                VALIDATE_ASSET_COMPLIANCE, { assetId });
            if (this.dialog.assetId() === assetId) this.result.set(r.validateAssetCompliance);
        } catch (err) {
            const detail = err instanceof MutationError
                ? err.userErrors.map(u => resolveErrorMessage(u, (k, p) => this.translate.instant(k, p))).join('; ')
                : this.translate.instant('common.errorGeneric');
            this.messageService.add({ severity: 'error', summary: this.translate.instant('common.error'), detail });
            this.dialog.close();
        } finally {
            this.loading.set(false);
        }
    }
}
